// The public leaderboard at /e/:slug — the clubhouse television and the link a golfer's
// spouse follows from the car park. NO AUTHENTICATION of any kind, by design: nobody is
// making an account to find out whether their husband is winning.
//
// One RPC, `public_leaderboard(p_slug)`, is the entire data path. It is security-definer
// and never returns the join code, which is what lets this page be genuinely public
// without exposing the write surface.
//
// NO RANKING LOGIC LIVES HERE. Everything below the fetch is a mapping into
// `@shared/sync/leaderboard`, the same function the phone app renders from, so the screen
// on the wall and the phone in a player's pocket cannot disagree about who is leading.
// The only work this file does with the numbers is turn snake_case into camelCase.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, errText } from '../lib/supabase';
import {
  leaderboard,
  fieldProgress,
  type LeaderboardInput,
  type LeaderboardTeam,
  type LeaderboardEntrant,
} from '@shared/sync/leaderboard';
import {
  courseFromPlatform,
  hasRealStrokeIndexes,
  roundFromPlatform,
} from '@shared/sync/toDomain';
import {
  scoringTeamRowsFromPayload,
  scoringTeamCashRowsFromPayload,
  fieldSkinsFromPayload,
} from '@shared/sync/leaderboard';
import type { GameConfig } from '@shared/engine/types';
import { fmtTeeTime } from '@shared/lib/teeTime';
import { BoardStyles, Standings, Contests, type ContestLine } from '../components/Standings';
import { TeamStandings } from '../components/TeamStandings';
import { TeamCashStandings } from '../components/TeamCashStandings';
import { FieldSkinsBoard } from '../components/FieldSkins';
import type { computeGroupMoney } from '../components/GroupMoney';
import { LoadingStandings } from '../components/Loading';
import { teamRuleLine } from '../lib/scoringTeams';

/** How often to ask the server again. Polling beats realtime here: it is one anonymous
 *  read, it survives a flaky clubhouse wifi, and a dropped socket cannot silently freeze
 *  a board that everyone in the room believes is live. */
const POLL_MS = 20_000;

// ── What the RPC actually returns ────────────────────────────────────────────
// Mirrors public_leaderboard() as of supabase/migrations/0008_public_leaderboard_entrants.sql.
// Written defensively: this is JSON off the wire, so anything the SQL could return null or
// omit is optional here.

/** Added by 0008. `id` matches `entrant_id` in `scores`, which is what makes an individual
 *  event rankable at all. `name` is null for a seat a sponsor bought and never filled. */
type RpcEntrant = {
  id: string;
  name?: string | null;
  course_handicap?: number | null;
  withdrawn?: boolean | null;
  /** The tiered team this player scores for — independent of which group they walk with.
   *  Absent on an older RPC, or on an event where tiers were never turned on. */
  scoring_team_id?: string | null;
  /** 1 = A. Set by a draft; absent wherever `scoring_team_id` is. */
  tier?: number | null;
};

type RpcScore = { entrant_id: string; hole_idx: number; gross: number };

type RpcTeam = {
  id: string;
  name: string;
  slot_label?: string | null;
  /** 'HH:MM:SS' or null. Only meaningful when the event's `start_mode` is 'tee_times'. */
  tee_time?: string | null;
  /** The foursome's own signup captain — SAME mode reads this as the scoring team's
   *  captain. Name only; contact info never leaves the console. */
  captain_name?: string | null;
  entrants?: RpcEntrant[] | null;
  /** The pre-0008 list of bare names. Kept by the RPC; read here only as a fallback. */
  players?: string[] | null;
  scores?: RpcScore[] | null;
  /** This group's registered side games — `round_games.config` verbatim. Optional because
   *  an event where nobody set any up returns an empty list, and an older RPC returns none. */
  games?: Array<{ id: string; config: GameConfig }> | null;
};

/** A tiered scoring team — not one of the `teams` above, which are the groups that walk
 *  together. Read only by `scoringTeamRowsFromPayload`; this page never inspects it itself. */
type RpcScoringTeam = { id: string; name: string; seq: number; captain_name?: string | null };

type RpcTournament = {
  name: string;
  starts_on?: string | null;
  format: 'scramble' | 'individual';
  status: 'draft' | 'open' | 'live' | 'final' | 'cancelled';
  scoring: 'gross' | 'net';
  course_name?: string | null;
  hole_pars?: number[] | null;
  /** Stroke index per played hole. Still null on an event where the host never entered them. */
  hole_indexes?: (number | null)[] | null;
  hole_number_offset?: number | null;
  /** Whether the host chose to put side-game winnings on a public screen. Off by default,
   *  and absent on an older RPC — both of which mean no money column. */
  show_money?: boolean | null;
  /** 'shotgun' (default) or 'tee_times'. Absent on an older RPC, which only ever meant
   *  shotgun. */
  start_mode?: 'shotgun' | 'tee_times' | null;
  /** Whether a Teams board belongs on this page at all. */
  tiers_enabled?: boolean | null;
  team_rule?: 'best1' | 'best2' | 'aggregate' | null;
  team_basis?: 'gross' | 'net' | null;
  /** 'separate' (default, and what an older RPC always meant) — the Teams board is a
   *  second roster cut across the walking groups below, drafted by handicap. 'same' — there
   *  is no second roster; each walking group IS its own team, so the Cash table (which only
   *  ever ranks a SEPARATE roster's pooled winnings) never applies. */
  team_mode?: 'separate' | 'same' | null;
  /** off (default; also what an older RPC with no such column always meant): no field-wide
   *  skins pool. gross/net: the whole field plays one pool across every card. See
   *  `fieldSkinsFromPayload` (@shared/sync/leaderboard) and supabase/migrations/
   *  0024_field_skins.sql. */
  field_skins?: 'off' | 'gross' | 'net' | null;
  /** Cents PER PLAYER; only meaningful when `field_skins` is not 'off'. */
  field_skins_buy_in?: number | null;
  /** true (default): a tied hole rolls into the next outright win. false: void. */
  field_skins_carry?: boolean | null;
  /** player (default): the pool above is every entrant. team: one row per team instead — see
   *  supabase/migrations/0025_field_skins_team.sql and `fieldSkinsFromPayload`. */
  field_skins_unit?: 'player' | 'team' | null;
  /** Cents; only meaningful when > 0. Every skin then pays this fixed amount instead of
   *  splitting `field_skins_buy_in`'s pot. */
  field_skins_per_skin?: number | null;
};

type RpcPayload = {
  tournament: RpcTournament;
  teams?: RpcTeam[] | null;
  contests?: ContestLine[] | null;
  /** The tiered scoring teams. Absent or empty on an event with tiers off. */
  scoring_teams?: RpcScoringTeam[] | null;
};

// ── RPC → the shared engine's input ──────────────────────────────────────────

/**
 * A team's entrants, as the engine wants them.
 *
 * SCRAMBLE — the team makes one score per hole and the engine takes the minimum across
 * whoever recorded it, so entrant identity only feeds the "who is on this team" subtitle
 * and, under net, the blended team handicap.
 *
 * INDIVIDUAL — every entrant is their own line, so the engine splits `scores` by
 * `entrantId`. That is exactly what `entrants[].id` from migration 0008 provides.
 *
 * A null `name` is a seat somebody bought and never filled; it is passed through as null so
 * the engine's own "TBD" convention renders it, rather than being invented here.
 *
 * Withdrawn players stay in the list. Their scores are on a card their team-mates played,
 * and dropping the person would take the hole with them. `withdrawn` is passed to the
 * engine, which unranks them and sorts them last — this page only has to render that.
 */
function entrantsFor(
  team: RpcTeam,
  format: 'scramble' | 'individual'
): { entrants: LeaderboardEntrant[]; withHandicap: number } {
  const rich = team.entrants ?? [];

  if (rich.length > 0) {
    return {
      entrants: rich.map((e) => ({
        id: e.id,
        // In a scramble the row is the TEAM — which the engine never marks withdrawn,
        // because a team plays on when a member walks in — so the only place a scramble
        // can say it is the subtitle. In an individual event the player has their own
        // row, which Standings greys off `row.withdrawn`, so the name is left clean.
        displayName:
          format === 'scramble' && e.withdrawn ? `${e.name?.trim() || 'TBD'} (WD)` : e.name ?? null,
        courseHandicap: typeof e.course_handicap === 'number' ? e.course_handicap : null,
        withdrawn: e.withdrawn === true,
      })),
      withHandicap: rich.filter((e) => typeof e.course_handicap === 'number').length,
    };
  }

  // Pre-0008 payload, or a team with nobody in it. Names only, so ids are synthetic: right
  // for a scramble, and for an individual event it shows each player waiting to start
  // rather than attributing anyone's score to the wrong person.
  return {
    entrants: (team.players ?? []).map((name, i) => ({
      id: `${team.id}:${i}`,
      displayName: name ?? null,
    })),
    withHandicap: 0,
  };
}

/**
 * Stroke indexes, or null if they are not usable.
 *
 * Refused rather than repaired, because `allocateStrokes` reads these as 1-based RANKS
 * (`strokeIndexes[i] <= rem`) and will happily allocate from a nonsense array:
 *
 *   • a short array concentrates a whole handicap onto the holes it does cover;
 *   • a null hole is not caught by Number.isFinite — `Number(null)` is 0 — and an SI of 0
 *     compares below every remainder, so that hole silently collects an extra stroke on
 *     every card in the field. Postgres smallint[] can hold nulls, so this is reachable.
 *
 * Requiring a genuine permutation of 1..n is the actual contract, and a course that has
 * half-entered its card gets an honest gross board instead of a subtly wrong net one.
 */
function usableStrokeIndexes(
  raw: (number | null)[] | null | undefined,
  holes: number
): number[] | null {
  if (!Array.isArray(raw) || holes === 0 || raw.length !== holes) return null;
  const seen = new Set<number>();
  for (const v of raw) {
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > holes) return null;
    if (seen.has(v)) return null;
    seen.add(v);
  }
  return raw as number[];
}

type Mapped = {
  input: LeaderboardInput;
  /** A net event served as gross because the data for netting it is not there. */
  netFellBack: boolean;
  /** Netted, but some players have no handicap on file and are playing off scratch. */
  netPartial: boolean;
};

function toInput(payload: RpcPayload): Mapped {
  const t = payload.tournament;
  const format = t.format === 'individual' ? 'individual' : 'scramble';
  const holePars = (t.hole_pars ?? []).map(Number);
  const strokeIndexes = usableStrokeIndexes(t.hole_indexes, holePars.length);

  let entrantCount = 0;
  let withHandicap = 0;

  const teams: LeaderboardTeam[] = (payload.teams ?? []).map((team) => {
    const scores = (team.scores ?? []).map((s) => ({
      entrantId: s.entrant_id,
      holeIdx: Number(s.hole_idx),
      gross: Number(s.gross),
    }));
    const mapped = entrantsFor(team, format);
    entrantCount += mapped.entrants.length;
    withHandicap += mapped.withHandicap;

    return {
      id: team.id,
      name: team.name,
      slotLabel: team.slot_label ?? null,
      entrants: mapped.entrants,
      scores,
    };
  });

  // Net needs stroke indexes AND somebody to give strokes to. Without either, the engine
  // would hand out zero shots and return a "net" identical to gross — a wrong figure under
  // a NET heading, which is worse than an honest gross one. So fall back and say so.
  const wantsNet = t.scoring === 'net';
  const canNet = wantsNet && strokeIndexes !== null && withHandicap > 0;

  return {
    input: {
      format,
      scoring: canNet ? 'net' : 'gross',
      holePars,
      // The engine does the rest: one blended handicap for a scramble team, individual
      // allocations for a stroke-play field. None of that is reimplemented here.
      holeStrokeIndexes: canNet ? strokeIndexes : undefined,
      teams,
    },
    netFellBack: wantsNet && !canNet,
    netPartial: canNet && withHandicap < entrantCount,
  };
}

// ── Side-game money ──────────────────────────────────────────────────────────

type BoardMoney = {
  /** Cents by ENTRANT id. Sums to zero inside each group, never across the field. */
  byEntrant: Record<string, number>;
  /** A group had a net game that had to be settled gross. Worth saying out loud. */
  anyDowngraded: boolean;
};

/**
 * Every group's side-game money, computed per group and merged into one lookup.
 *
 * PER GROUP IS THE WHOLE POINT. Skins are played against the three people in your cart, not
 * against a foursome two hours ahead that you will never see. So each team's round is built
 * on its own — its entrants, its scores, its games — and the ledgers are simply collected.
 * The figures for one group always sum to zero; the field's total is zero by construction
 * and means nothing.
 *
 * Returns null when there is nothing to show, which is also what a host who left
 * `show_money` off gets. A charity outing does not want "Bob −$45" beside the raffle.
 *
 * `compute` is passed in rather than imported, because importing it would pull all nineteen
 * engines into the bundle every clubhouse screen and every phone in a car park downloads —
 * for a feature most events leave switched off. See `wantsMoney` below.
 */
function boardMoney(
  payload: RpcPayload,
  compute: typeof computeGroupMoney
): BoardMoney | null {
  const t = payload.tournament;

  const holePars = (t.hole_pars ?? []).map(Number);
  if (holePars.length === 0) return null;

  // Checked here rather than trusted, for the reason spelled out on `usableStrokeIndexes`
  // above: a null in a smallint[] arrives as 0 and `allocateStrokes` reads it as the
  // hardest rank there is. `computeGroupMoney` settles a net game gross when this is false.
  const strokeIndexesUsable = hasRealStrokeIndexes(
    (t.hole_indexes ?? undefined) as number[] | undefined,
    holePars.length
  );

  const course = courseFromPlatform({
    name: t.course_name ?? 'Course',
    holePars,
    holeStrokeIndexes: (t.hole_indexes ?? null) as number[] | null,
  });

  const byEntrant: Record<string, number> = {};
  let anyGames = false;
  let anyDowngraded = false;

  for (const team of payload.teams ?? []) {
    const games = (team.games ?? []).filter((g) => g && g.config);
    if (games.length === 0) continue;
    anyGames = true;

    const round = roundFromPlatform(
      {
        id: team.id,
        entrants: (team.entrants ?? []).map((e) => ({
          id: e.id,
          displayName: e.name ?? null,
          courseHandicap: typeof e.course_handicap === 'number' ? e.course_handicap : null,
        })),
        scores: (team.scores ?? []).map((s) => ({
          entrantId: s.entrant_id,
          holeIdx: Number(s.hole_idx),
          gross: Number(s.gross),
        })),
        games,
      },
      holePars.length
    );

    const result = compute(course, round, strokeIndexesUsable);
    anyDowngraded = anyDowngraded || result.anyDowngraded;
    for (const [entrantId, cents] of Object.entries(result.money)) {
      byEntrant[entrantId] = (byEntrant[entrantId] ?? 0) + cents;
    }
  }

  return anyGames ? { byEntrant, anyDowngraded } : null;
}

// ── Presentation helpers ─────────────────────────────────────────────────────

/** 'YYYY-MM-DD' straight from a Postgres date. Split by hand: `new Date('2026-08-07')`
 *  parses as UTC midnight and shows the day before to anyone west of Greenwich. */
function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const STATUS_TEXT: Record<RpcTournament['status'], string> = {
  draft: 'Draft',
  open: 'Not started',
  live: 'Live',
  final: 'Final',
  cancelled: 'Cancelled',
};

function ago(seconds: number): string {
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

// ── The page ─────────────────────────────────────────────────────────────────

export default function LeaderboardPage() {
  const { slug } = useParams<{ slug: string }>();

  const [payload, setPayload] = useState<RpcPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Guards a late response from a poll that was in flight when the component unmounted or
  // the slug changed — otherwise a slow request can overwrite a newer board.
  const generation = useRef(0);

  const load = useCallback(async () => {
    if (!slug) {
      setError('No event at that link');
      setLoading(false);
      return;
    }
    const mine = ++generation.current;
    const { data, error: rpcError } = await supabase.rpc('public_leaderboard', { p_slug: slug });
    if (mine !== generation.current) return;

    if (rpcError) {
      // The RPC raises in plain English — "No event at that link", "That event has not
      // started yet" — so the server's own words are the friendliest thing to show.
      setError(errText(rpcError));
      setLoading(false);
      return;
    }
    const next = data as RpcPayload | null;
    if (!next || !next.tournament) {
      setError('No event at that link');
      setLoading(false);
      return;
    }
    setPayload(next);
    setError(null);
    setFetchedAt(Date.now());
    setLoading(false);
  }, [slug]);

  // Poll, but only while somebody is looking. A clubhouse screen sits on this URL all day
  // and a backgrounded tab has no business hitting the database every twenty seconds.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer !== null) return;
      timer = setInterval(() => {
        void load();
      }, POLL_MS);
    };
    const stop = () => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    void load();
    if (!document.hidden) start();

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Come back to a fresh board, not to whatever was on screen when the tab was hidden.
        void load();
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
      generation.current++;
    };
  }, [load]);

  // Ticks the "updated Xs ago" label. Also paused when hidden — a timer firing every
  // second behind a locked screen is pure battery for a phone on a golf cart.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => setNow(Date.now()), 1000);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => (document.hidden ? stop() : (setNow(Date.now()), start()));
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);

  const board = useMemo(() => {
    if (!payload) return null;
    const { input, netFellBack, netPartial } = toInput(payload);
    const rows = leaderboard(input);
    const holeCount = input.holePars.length;
    return {
      rows,
      holeCount,
      progress: fieldProgress(rows, holeCount),
      // The EFFECTIVE scoring — 'gross' when a net event could not be netted, so the board
      // never puts a NET heading over an unadjusted number.
      scoring: input.scoring,
      format: input.format,
      anyWithdrawn: rows.some((r) => r.withdrawn),
      netFellBack,
      netPartial,
    };
  }, [payload]);

  // ── Tiered scoring teams ───────────────────────────────────────────────────
  //
  // A second, independent board. `scoringTeamRowsFromPayload` reads the raw RPC payload
  // itself — the same one `toInput` above turns into the individual/group standings — so
  // this page does no ranking of its own for either board.
  // Same-mode teams ARE the foursomes, so the team id is the foursome id and carries its
  // tee time. In separate mode a scoring team has no single tee time, so nothing matches.
  const teeTimesByTeam = useMemo(() => {
    const out: Record<string, string> = {};
    for (const team of payload?.teams ?? []) {
      const label = fmtTeeTime(team.tee_time);
      if (label) out[team.id] = label;
    }
    return out;
  }, [payload]);

  const teamRows = useMemo(() => {
    if (!payload) return [];
    if (!payload.tournament.tiers_enabled) return [];
    // SAME mode's teams are the walking groups themselves — `scoring_teams` stays empty in
    // that mode, since nobody ever drafted a second roster, so the presence check that
    // gates SEPARATE mode would wrongly hide a same-mode board with a field already in it.
    if (payload.tournament.team_mode === 'same') {
      if (!payload.teams || payload.teams.length === 0) return [];
    } else if (!payload.scoring_teams || payload.scoring_teams.length === 0) {
      return [];
    }
    return scoringTeamRowsFromPayload(payload);
  }, [payload]);

  // ── Field-wide skins pool ──────────────────────────────────────────────────
  //
  // A third, independent board — no team of any kind, every entrant in one pool. Returns
  // null itself when the host has field skins off, so this only has to render conditionally
  // on the result, never re-check the switch.
  const fieldSkinsResult = useMemo(() => {
    if (!payload) return null;
    return fieldSkinsFromPayload(payload);
  }, [payload]);

  // ── Side-game money ────────────────────────────────────────────────────────
  //
  // Kept out of `board` on purpose. The stroke-play standings are one thing and the side
  // games are another — different scope (the field vs one foursome), different source
  // (the ranking engine vs the game engines), and nothing in the ranking depends on this.

  /**
   * Whether this event wants money at all, decided before a byte of it is fetched.
   *
   * `show_money` defaults to OFF, so most events never load the engines. A scramble never
   * does either: its rows are TEAMS, a group's money nets to zero within itself, and the
   * column would be a wall of dashes. Money is a per-person figure and belongs on a board
   * whose rows are people.
   */
  const wantsMoney =
    payload?.tournament.show_money === true && payload.tournament.format === 'individual';

  const [engines, setEngines] = useState<{ compute: typeof computeGroupMoney } | null>(null);

  useEffect(() => {
    if (!wantsMoney || engines) return;
    let live = true;
    void import('../components/GroupMoney')
      .then((m) => {
        if (live) setEngines({ compute: m.computeGroupMoney });
      })
      // A board that cannot fetch the engines shows the standings without a money column,
      // which is the same thing it shows an event that never turned money on.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [wantsMoney, engines]);

  const money = useMemo(
    () => (payload && wantsMoney && engines ? boardMoney(payload, engines.compute) : null),
    [payload, wantsMoney, engines]
  );

  // ── Team cash board ────────────────────────────────────────────────────────
  //
  // SEPARATE mode only — a SAME-mode "team" is just a foursome, and ranking foursomes by
  // cash would tell a host nothing a scramble game doesn't already settle inside the group.
  // Fed with the exact per-entrant figures `money` above already computed for the
  // individual board's Games column, never a second pass over the same round data.
  const cashRows = useMemo(() => {
    if (!payload || !money) return [];
    if (payload.tournament.team_mode === 'same') return [];
    if (!payload.scoring_teams || payload.scoring_teams.length === 0) return [];
    return scoringTeamCashRowsFromPayload(payload, money.byEntrant);
  }, [payload, money]);

  if (loading && !payload) {
    return <LoadingStandings />;
  }

  // A bad slug, a draft event, or the network being off in a clubhouse basement. All three
  // want a sentence a golfer's spouse can read, never a stack trace.
  if (error && !payload) {
    return (
      <div className="wrap">
        <h1>4sum</h1>
        <div className="card">
          <p style={{ margin: '0 0 12px', fontWeight: 700 }}>{error}</p>
          <p className="muted" style={{ margin: '0 0 16px', color: 'var(--ink-dim)' }}>
            Check the link the host sent you. If the event has not begun, standings appear
            once it is under way.
          </p>
          <button onClick={() => void load()}>Try again</button>
        </div>
      </div>
    );
  }

  if (!payload || !board) return null;

  const t = payload.tournament;
  const date = fmtDate(t.starts_on);
  const seconds = fetchedAt === null ? null : Math.max(0, Math.round((now - fetchedAt) / 1000));
  const stale = seconds !== null && seconds > POLL_MS / 1000 + 15;
  const statusClass =
    t.status === 'live' ? 'lb-chip live' : t.status === 'final' ? 'lb-chip final' : 'lb-chip';

  return (
    <div className="wrap lb-page">
      <BoardStyles />

      <header className="lb-head">
        <div>
          <h1 className="lb-title">{t.name}</h1>
          <p className="lb-sub">
            {t.course_name ? <span>{t.course_name}</span> : null}
            {t.course_name && date ? <span className="lb-dot">•</span> : null}
            {date ? <span>{date}</span> : null}
            <span className={statusClass}>{STATUS_TEXT[t.status] ?? t.status}</span>
            <span className="lb-chip">
              {board.format === 'scramble' ? 'Scramble' : 'Individual'} ·{' '}
              {board.scoring === 'net' ? 'Net' : 'Gross'}
            </span>
          </p>
        </div>
        <div className="lb-meta">
          <div>
            <span className={stale ? 'lb-pulse stale' : 'lb-pulse'} />
            {seconds === null ? 'updating…' : `updated ${ago(seconds)}`}
          </div>
          <div style={{ marginTop: 2 }}>
            {board.progress.finished} of {board.progress.total} finished
            {board.progress.started > board.progress.finished
              ? ` · ${board.progress.started - board.progress.finished} on the course`
              : ''}
          </div>
        </div>
      </header>

      <Standings
        rows={board.rows}
        holeCount={board.holeCount}
        scoring={board.scoring}
        money={money?.byEntrant ?? null}
      />

      {board.progress.started === 0 && board.rows.length > 0 ? (
        <p className="muted lb-foot">
          Nobody has holed out yet. Positions appear as scores come in.
        </p>
      ) : null}

      {/* A second board, independent of the one above — tiered teams cut across the groups
          that walk together, so a player's own line up there and their team's line down
          here can move in different directions. */}
      {teamRows.length > 0 ? (
        <section className="lb-section">
          <h2>Teams</h2>
          <TeamStandings
            rows={teamRows}
            holeCount={board.holeCount}
            headerLine={teamRuleLine(t.team_rule, t.team_basis)}
            teeTimes={t.start_mode === 'tee_times' ? teeTimesByTeam : undefined}
            showTiers={t.team_mode !== 'same'}
          />
          {/* Cash ranks the same rosters by pooled side-game winnings instead of golf — see
              the file header on TeamCashStandings for why that is a second table rather
              than a column on the one above. */}
          {cashRows.length > 0 ? <TeamCashStandings rows={cashRows} /> : null}
        </section>
      ) : null}

      {/* Field skins cuts across everything above — no team of any kind (player unit) or a
          second roster of its own (team unit) — so it gets its own section rather than a
          column on either board. */}
      {fieldSkinsResult ? (
        <section className="lb-section">
          <h2>{t.field_skins_unit === 'team' ? 'Team skins' : 'Skins'}</h2>
          <FieldSkinsBoard
            result={fieldSkinsResult}
            basis={t.field_skins === 'net' ? 'net' : 'gross'}
            unit={t.field_skins_unit === 'team' ? 'team' : 'player'}
          />
        </section>
      ) : null}

      <Contests contests={payload.contests ?? []} holeNumberOffset={t.hole_number_offset ?? 0} />

      {/* Said plainly, because a stranger reading the board will otherwise try to add the
          column up across the field. It only ever balances inside one foursome. */}
      {money ? (
        <p className="muted lb-note">
          GAMES is each player&rsquo;s side-game money, settled inside their own foursome. It is
          not part of the event standings.
        </p>
      ) : null}
      {money?.anyDowngraded ? (
        <p className="muted lb-note">
          Some foursomes set their games up net. Those are shown gross — the course&rsquo;s
          stroke indexes are not on file, and guessing them would move real money.
        </p>
      ) : null}

      {/* Only shown when the event is DECLARED net but could not be netted — the board is
          gross above, and saying so beats a NET heading over unadjusted figures. */}
      {board.netFellBack ? (
        <p className="muted lb-note">
          This event is scored net, but stroke indexes or player handicaps have not been
          entered yet. The figures above are gross until they are.
        </p>
      ) : null}
      {board.netPartial ? (
        <p className="muted lb-note">
          Some players have no course strokes on file and are shown off scratch.
        </p>
      ) : null}
      {/* Only individual play produces a withdrawn ROW — a scramble team is never
          withdrawn, so there the WD is in the subtitle and needs no key. */}
      {board.anyWithdrawn ? (
        <p className="muted lb-note">
          Greyed lines are players who withdrew. They keep the holes they played but do not
          hold a position.
        </p>
      ) : null}

      {/* The polling cadence, said out loud, so nobody stands in front of the screen
          wondering whether it has frozen. */}
      <p className="muted lb-note">Standings refresh automatically every 20 seconds.</p>
    </div>
  );
}
